import React from "react";
import ReactDOM from "react-dom/client";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";

import "./styles.css";
import heroTemplate from "./assets/hero-template.png";
import dbsLogo from "./assets/dbs.webp";
import dellLogo from "./assets/dell.webp";
import ftcSolarLogo from "./assets/ftcsolar.webp";
import iciciLogo from "./assets/icici.webp";
import ikeaLogo from "./assets/ikea.webp";
import indusindLogo from "./assets/indusland.webp";
import nexMoveLogo from "./assets/nexmove.webp";
import diopzLogo from "./assets/diopz.png";
import stayawyLogo from "./assets/stayawy.png";
import singtelLogo from "./assets/singtel.webp";
import tableSpaceLogo from "./assets/tablespace.webp";
// import weworkLogo from "./assets/wework.webp";

import About from "./about.jsx";
import Careers from "./careers.jsx";
import Gallery from "./gallery.jsx";
import Inquiry from "./inquiry.jsx";
import LoginPage from "./login.jsx";
import NoInternet, { ConnectionBoundary } from "./no-internet.jsx";
import PermissionDenied from "./permission-denied.jsx";
import SessionExpired from "./session-expired.jsx";
import NoSearchResults from "./no-search-results.jsx";
import EmptyState from "./empty-state.jsx";
import LoadingState from "./loading-state.jsx";
import ErrorPage from "./error-page.jsx";
import FormValidation from "./form-validation.jsx";
import SuccessState from "./success-state.jsx";
import SlowNetwork from "./slow-network.jsx";
import { AppStateBoundary, AppErrorBoundary } from "./app-state.jsx";
import Admin from "./admin.jsx";
import Services from "./services.jsx";
import OurBrands from "./our-brands.jsx";
import { PrivacyPolicy, TermsConditions } from "./legal.jsx";
import { SiteHeader, SiteFooter } from "./chrome.jsx";
import { FaArrowRight } from "react-icons/fa6";
import "./overrides.css";
import "./typography.css";
import "./popups.css";

// State routes do not mount SiteHeader, so initialize their theme here too.
try {
  document.documentElement.dataset.theme =
    localStorage.getItem("hallef-theme") === "dark" ? "dark" : "light";
} catch {
  document.documentElement.dataset.theme = "light";
}

const brands = [
  {
    number: "01",
    // name: "NEX MOVE",
    meta: "LOGISTICS PLATFORM",
    title: "Move work forward.",
    body: "A logistics platform for households and businesses, bringing booking, instant quotes and transport opportunities into one clear flow.",
    logo: nexMoveLogo,
    logoVariant: "wide",
    websiteUrl: "https://nexmove.in/home",
  },
  {
    number: "02",
    name: "Diopz",
    meta: "HRMS PLATFORM",
    title: "Manage every employee journey.",
    body: "A central HRMS for attendance, payroll, leave, recruitment and employee management with AI support through Brigenza.",
    logo: diopzLogo,
    logoVariant: "diopz",
    websiteUrl: "https://diopz.com/",
  },
  {
    number: "03",
    // name: "stayawy",
    // meta: "TRAVEL AND STAYS",
    title: "Book better spaces with less effort.",
    body: "A stay-booking app for discovering comfortable accommodation, comparing property details and managing the trip from one clear place.",
    logo: stayawyLogo,
    logoVariant: "stayawy",
    websiteUrl: "https://stayawy.com/",
  },
];

const clients = [
  ["Singtel", singtelLogo],
  ["ICICI Bank", iciciLogo],
  ["IndusInd Bank", indusindLogo],
  ["Dell", dellLogo],
  ["IKEA", ikeaLogo],
  ["DBS", dbsLogo],
  ["FTC Solar", ftcSolarLogo],
  ["Table Space", tableSpaceLogo],
  // ["WeWork", weworkLogo],
];

function Home() {
  return (
    <>
      <SiteHeader />
      <main className="home-page">
        <section className="hero" id="home">
          <div className="hero-copy">
            <h1>Smarter<br /><span>Technology</span><br />for a Better<br />Tomorrow<span className="headline-dot">.</span></h1>
            <div className="hero-actions">
              <a className="primary-button" href="/inquiry">Let's Build Together <FaArrowRight aria-hidden="true" /></a>
            </div>
          </div>
          <div className="hero-visual new-hero-visual" aria-label="Modern connected city and technology artwork">
            <img className="hero-template-image" src={heroTemplate} alt="" aria-hidden="true" />
          </div>
        </section>

        <section className="about-strip" id="about">
          <div className="about-lead">
            <p className="about-kicker">OUR APPROACH</p>
            <h2>Hallef is an innovation company focused on making work and everyday life <span>simpler.</span></h2>
          </div>
          <div className="principles">
            {[
              ["01", "Find the friction.", "We identify real problems that matter."],
              ["02", "Build the clear path.", "We design simple, effective solutions."],
              ["03", "Automate what slows people down.", "We use technology to create faster, smarter workflows."],
            ].map(([number, text, description]) => (
              <article key={number} data-number={number}>
                <strong>{text}</strong>
                <p>{description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="brands-section" id="brands">
          <div className="section-intro">
            <div>
              <p className="section-kicker">OUR BRANDS</p>
              <h2>One Purpose. <span>Three Powerful Brands.</span></h2>
            </div>
            {/* <div className="section-note"><a href="/services">Explore all brands <FaArrowRight aria-hidden="true" /></a></div> */}
          </div>
          <div className="brand-grid">
            {brands.map((brand) => (
              <article
                className={`brand-card brand-card-${brand.logoVariant}`}
                key={brand.number}
              >
                <div className="brand-head">
                  <span className={`brand-icon brand-icon-${brand.logoVariant}`} aria-hidden="true">
                    <img src={brand.logo} alt="" />
                  </span>
                  <div>
                    <h3>{brand.name}</h3>
                    <p>{brand.meta}</p>
                  </div>
                </div>
                <h2>{brand.title}</h2>
                <p className="brand-body">{brand.body}</p>
                <a href={brand.websiteUrl} target="_blank" rel="noopener noreferrer">
                  Explore {brand.name} <FaArrowRight aria-hidden="true" />
                </a>
                <div className="card-visual" aria-hidden="true"><span /><span /><span /></div>
              </article>
            ))}
          </div>
        </section>

        <section className="clients-section" aria-labelledby="clients-title">
          <div className="clients-container">
            <div className="clients-header">
              <p className="clients-kicker">TRUSTED PARTNERS</p>
              <h2 className="clients-title" id="clients-title">Our Clients &amp; Trust Partners</h2>
              <p className="clients-subtitle">We are trusted by leading global and Indian organizations.</p>
            </div>
            <div className="clients-logo-viewport">
            <div className="clients-grid" aria-label="Client and partner logos">
              {clients.map(([name, logo]) => (
                <article className="client-card" key={name}>
                  <img src={logo} alt={name} />
                  {/* <span>{name}</span> */}
                </article>
              ))}
              {clients.map(([name, logo]) => (
                <article className="client-card client-card-loop" key={`loop-${name}`} aria-hidden="true">
                  <img src={logo} alt="" />
                </article>
              ))}
            </div>
            </div>
          </div>
        </section>
      </main>
    <SiteFooter />
    </>
  );
}

function ProtectedAdmin() {
  const token = localStorage.getItem("hallef-admin-token");
  const role = (localStorage.getItem("hallef-user-role") || "").trim().toLowerCase();

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  try {
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    if (payload.exp && payload.exp * 1000 <= Date.now()) return <SessionExpired />;
  } catch {
    return <SessionExpired />;
  }
  if (role !== "admin") return <PermissionDenied />;
  return <Admin />;
}

ReactDOM.createRoot(
  document.getElementById("root")
).render(
  <React.StrictMode>
    <BrowserRouter>
      <ConnectionBoundary>
        <AppStateBoundary>
        <AppErrorBoundary>
        <Routes>
          <Route path="/no-internet" element={<NoInternet />} />
          <Route path="/slow-network" element={<SlowNetwork />} />
          <Route path="/permission-denied" element={<PermissionDenied />} />
          <Route path="/session-expired" element={<SessionExpired />} />
          <Route path="/no-search-results" element={<NoSearchResults />} />
          <Route path="/empty-state" element={<EmptyState />} />
          <Route path="/loading" element={<LoadingState />} />
          <Route path="/error" element={<ErrorPage />} />
          <Route path="/form-validation" element={<FormValidation />} />
          <Route path="/success" element={<SuccessState />} />
          <Route
            path="/"
            element={<Home />}
          />

          <Route path="/about" element={<About />} />

          <Route path="/services" element={<Services />} />

          <Route path="/our-brands" element={<OurBrands />} />

          <Route path="/careers" element={<Careers />} />

          <Route path="/gallery" element={<Gallery />} />

          <Route path="/inquiry" element={<Inquiry />} />

          <Route path="/privacy" element={<PrivacyPolicy />} />

          <Route path="/terms" element={<TermsConditions />} />

          <Route
            path="/login"
            element={<LoginPage />}
          />

          <Route
            path="/admin"
            element={<ProtectedAdmin />}
          />

          <Route
            path="*"
            element={<Navigate to="/" replace />}
          />
        </Routes>
        </AppErrorBoundary>
        </AppStateBoundary>
      </ConnectionBoundary>
    </BrowserRouter>
  </React.StrictMode>
);
